import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Container, Row, Col } from 'react-bootstrap';
import NoteSettings from './NoteSettings';
import NoteReview from './NoteReview';
import API from '../API';
import './Note.css';

/**
 * Note - メインのNoteタブコンポーネント
 * 左側: Settings（編集可能なセッティング一覧）
 * 右側: Review（振り返り入力 + AIアドバイス）
 */
class Note extends Component {

    constructor(props) {
        super(props);
        this.state = {
            // セッティング ([{ key, value }, ...])
            settings: [],
            // 振り返り
            reviewGoal: '',
            reviewBad: '',
            reviewWhy: '',
            reviewAction: '',
            // AIアドバイス
            aiAdvice: '',
            aiAdviceGeneratedAt: '',
            adviceError: null,
            isGeneratingAdvice: false,
            // ローディング
            isLoadingSetting: false,
            isLoadingReview: false,
            // 保存
            isSaving: false,
            saveMessage: null,
            saveMessageType: null,
        };
    }

    componentDidMount() {
        // 初回マウント時にも選択中のプラクティスデータを反映
        if (this.props.target) {
            this.loadTargetData(this.props.target);
        }
    }

    componentDidUpdate(prevProps) {
        // 選択されたプラクティスが変わったら、データを反映
        if (this.props.target && this.props.target !== prevProps.target) {
            this.loadTargetData(this.props.target);
        }
    }

    // 選択中のプラクティスデータをstateに反映
    loadTargetData = (target) => {
        // セッティングの展開（[{"key":"value"}] → [{key, value}]）
        const rawSettings = target.setting || [];
        const settings = rawSettings.flatMap(obj =>
            Object.entries(obj).map(([key, value]) => ({ key, value }))
        );

        this.setState({
            settings,
            reviewGoal: target.review_goal || '',
            reviewBad: target.review_bad || '',
            reviewWhy: target.review_why || '',
            reviewAction: target.review_action || '',
            aiAdvice: target.ai_advice || '',
            aiAdviceGeneratedAt: target.ai_advice_generated_at || '',
            adviceError: null,
            saveMessage: null,
        });
    }

    // セッティング変更
    handleSettingsChange = (newSettings) => {
        this.setState({ settings: newSettings });
    }

    // 最新セッティングのロード
    handleLoadLastSetting = () => {
        if (!this.props.user) return;
        this.setState({ isLoadingSetting: true });
        API.loadLastSetting({
            token: this.props.user.token,
            event_id: this.props.target?.event_id,
            onSuccess: (response) => {
                // foundフラグがfalseの場合、データなしとして処理
                if (response.data?.found === false) {
                    console.warn('セッティングデータなし:', response.data.message);
                    this.props.dispatch({ type: 'SET_MESSAGE', msgType: 'warning', message: 'セッティングデータが見つかりませんでした。' });
                    this.setState({ isLoadingSetting: false });
                    return;
                }
                const rawSettings = response.data?.setting || response.data || [];
                let settings = [];
                if (Array.isArray(rawSettings)) {
                    settings = rawSettings.flatMap(obj =>
                        Object.entries(obj).map(([key, value]) => ({ key, value: String(value) }))
                    );
                }
                this.setState({ settings, isLoadingSetting: false });
            },
            onError: (error) => {
                console.error('セッティングのロードに失敗:', error);
                this.setState({ isLoadingSetting: false });
            },
        });
    }

    // 振り返り変更
    handleReviewChange = (field, value) => {
        this.setState({ [field]: value });
    }

    // 最新の振り返りをロード
    handleLoadLastReview = () => {
        if (!this.props.user) return;
        this.setState({ isLoadingReview: true });
        API.loadLastReview({
            token: this.props.user.token,
            event_id: this.props.target?.event_id,
            onSuccess: (response) => {
                // foundフラグがfalseの場合、データなしとして処理
                if (response.data?.found === false) {
                    console.warn('振り返りデータなし:', response.data.message);
                    this.props.dispatch({ type: 'SET_MESSAGE', msgType: 'warning', message: '振り返りデータが見つかりませんでした。' });
                    this.setState({ isLoadingReview: false });
                    return;
                }
                const data = response.data;
                this.setState({
                    reviewGoal: data.review_goal || '',
                    reviewBad: data.review_bad || '',
                    reviewWhy: data.review_why || '',
                    reviewAction: data.review_action || '',
                    isLoadingReview: false,
                });
            },
            onError: (error) => {
                console.error('振り返りのロードに失敗:', error);
                this.setState({ isLoadingReview: false });
            },
        });
    }

    // AIアドバイスを生成
    handleGenerateAdvice = () => {
        if (!this.props.user || !this.props.target) return;
        this.setState({ isGeneratingAdvice: true, adviceError: null });
        API.generateAdvice({
            token: this.props.user.token,
            practiceId: this.props.target.id,
            onSuccess: (response) => {
                const data = response.data;
                this.setState({
                    aiAdvice: data.ai_advice || '',
                    aiAdviceGeneratedAt: data.ai_advice_generated_at || '',
                    isGeneratingAdvice: false,
                });
                // Redux storeのtargetも更新（タブ切替・画面遷移後も保持するため）
                this.props.dispatch({
                    type: 'UPDATE_TARGET_ADVICE',
                    ai_advice: data.ai_advice || '',
                    ai_advice_generated_at: data.ai_advice_generated_at || '',
                });
            },
            onError: (error) => {
                console.error('AIアドバイスの生成に失敗:', error);
                const msg = error?.response?.data?.error || 'アドバイスの生成に失敗しました';
                this.setState({
                    adviceError: msg,
                    isGeneratingAdvice: false,
                });
            },
        });
    }

    // 保存処理
    handleSave = () => {
        if (!this.props.user || !this.props.target) return;

        // セッティングの復元（[{key, value}] → [{"key":"value"}]）
        const settingPayload = this.state.settings
            .filter(item => item.key.trim() !== '')
            .map(item => ({ [item.key]: item.value }));

        this.setState({ isSaving: true, saveMessage: null });

        API.putPractice({
            user: this.props.user,
            practiceId: this.props.target.id,
            windDirection: this.props.target.wind_direction,
            windSpeed: this.props.target.wind_speed,
            windSpeedMin: this.props.target.wind_speed_min,
            calcMode: this.props.target.calc_mode,
            calcParm: this.props.target.calc_parm,
            buoy_coordinate: this.props.target.buoy_coordinate,
            event_id: this.props.target.event_id,
            setting: settingPayload,
            review_goal: this.state.reviewGoal,
            review_bad: this.state.reviewBad,
            review_why: this.state.reviewWhy,
            review_action: this.state.reviewAction,
            onSuccess: () => {
                this.props.dispatch({ type: 'SET_MESSAGE', msgType: 'info', message: '保存しました' });
                this.setState({ isSaving: false });
            },
            onError: (error) => {
                console.error('保存に失敗:', error);
                this.props.dispatch({ type: 'SET_MESSAGE', msgType: 'danger', message: '保存に失敗しました' });
                this.setState({ isSaving: false });
            },
        });
    }

    render() {
        if (!this.props.target) {
            return <div className="note-no-data">データを選択してください</div>;
        }

        return (
            <Container className="note-container">
                <Row>
                    {/* 左側: Settings */}
                    <Col xs={6}>
                        <NoteSettings
                            settings={this.state.settings}
                            onChange={this.handleSettingsChange}
                            onLoadLastSetting={this.handleLoadLastSetting}
                            isLoading={this.state.isLoadingSetting}
                        />
                    </Col>

                    {/* 右側: Review + AIアドバイス */}
                    <Col xs={6}>
                        <NoteReview
                            reviewGoal={this.state.reviewGoal}
                            reviewBad={this.state.reviewBad}
                            reviewWhy={this.state.reviewWhy}
                            reviewAction={this.state.reviewAction}
                            onChangeReview={this.handleReviewChange}
                            onLoadLastReview={this.handleLoadLastReview}
                            isLoadingReview={this.state.isLoadingReview}
                            aiAdvice={this.state.aiAdvice}
                            aiAdviceGeneratedAt={this.state.aiAdviceGeneratedAt}
                            adviceError={this.state.adviceError}
                            isGeneratingAdvice={this.state.isGeneratingAdvice}
                            onGenerateAdvice={this.handleGenerateAdvice}
                        />
                    </Col>
                </Row>

                {/* 保存ボタン */}
                <div className="note-save-area">
                    <button
                        className="btn-save-note"
                        onClick={this.handleSave}
                        disabled={this.state.isSaving}
                    >
                        {this.state.isSaving ? '保存中...' : '保存'}
                    </button>
                    {this.state.saveMessage && (
                        <div className={`save-message ${this.state.saveMessageType}`}>
                            {this.state.saveMessage}
                        </div>
                    )}
                </div>
            </Container>
        );
    }
}

const mapStateToProps = state => {
    return {
        target: state.Main.target,
        user: state.auth.user,
    };
}

export default connect(mapStateToProps)(Note);
